import {
  KEY_TONE_CLASS,
  keyExpiryTone,
  platformLabel,
} from "@via/pages/projects/metadata/configuration/data-import/utils/form";
import { formatDateString } from "@via/utils/helpers";
import type { VmetaImportLogin } from "common";
import type { FC } from "react";

export const KeyExpiryCell: FC<{
  platform: string | null;
  keyExpiresAt: string | null;
}> = ({ platform, keyExpiresAt }) => {
  if (platform !== "mydatahelps") return null;
  const tone = keyExpiryTone(keyExpiresAt);
  if (tone === "none")
    return <span className={KEY_TONE_CLASS.none}>No key</span>;
  return (
    <span className={KEY_TONE_CLASS[tone]}>
      {tone === "expired" ? "Expired " : "Expires "}
      {keyExpiresAt ? formatDateString(new Date(keyExpiresAt), "LLL") : ""}
    </span>
  );
};

const LoginList: FC<{
  items: VmetaImportLogin[];
  isLoading: boolean;
  onOpen: (item: VmetaImportLogin) => void;
  onNew: () => void;
}> = ({ items, isLoading, onOpen, onNew }) => (
  <section aria-labelledby="import-logins-heading" className="mt-8 space-y-3">
    <div className="flex items-center justify-between">
      <h3
        id="import-logins-heading"
        className="text-sm font-semibold text-gray-900">
        Credentials
      </h3>
      <button
        type="button"
        onClick={onNew}
        className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
        New credential
      </button>
    </div>
    <table className="min-w-full divide-y divide-gray-300 text-sm">
      <thead>
        <tr className="text-left font-semibold text-gray-900">
          <th className="px-3 py-2">Name</th>
          <th className="px-3 py-2">Platform</th>
          <th className="px-3 py-2">Belongs to</th>
          <th className="px-3 py-2">Key</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {isLoading && (
          <tr>
            <td colSpan={4} className="px-3 py-4 text-gray-500">
              Loading credentials
            </td>
          </tr>
        )}
        {!isLoading && items.length === 0 && (
          <tr>
            <td colSpan={4} className="px-3 py-4 text-gray-500">
              No credentials yet.
            </td>
          </tr>
        )}
        {items.map(item => (
          <tr key={item.id} aria-label={item.name}>
            <td className="px-3 py-2">
              {item.canManage ? (
                <button
                  type="button"
                  className="via-link text-left"
                  onClick={() => onOpen(item)}>
                  {item.name}
                </button>
              ) : (
                item.name
              )}
            </td>
            <td className="px-3 py-2">{platformLabel(item.platform)}</td>
            <td className="px-3 py-2">
              {item.scope === "personal"
                ? "Personal"
                : (item.organizationName ?? "Organization")}
            </td>
            <td className="px-3 py-2">
              <KeyExpiryCell
                platform={item.platform}
                keyExpiresAt={item.keyExpiresAt}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </section>
);

export default LoginList;
